import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Send, Users, TestTube2 } from "lucide-react";

export default function AdminNotifications() {
  const [stats, setStats] = useState({ total_subscriptions: 0, unique_users: 0 });
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("/");
  const [audience, setAudience] = useState("all");
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadStats = () =>
    api.get("/push/admin/stats").then((r) => setStats(r.data)).catch(() => {});

  useEffect(() => { loadStats(); }, []);

  const broadcast = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error("العنوان والنص مطلوبان");
      return;
    }
    setSending(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/push/admin/broadcast", {
        title: title.trim(),
        body: body.trim(),
        url: url || "/",
        audience,
      });
      setLastResult(data);
      toast.success(`تم الإرسال إلى ${data.sent} جهاز${data.failed ? ` (فشل ${data.failed})` : ""}`);
      loadStats();
    } catch (e) {
      toast.error(extractError(e, "فشل الإرسال"));
    } finally {
      setSending(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/push/admin/test", {});
      if (data.sent > 0) toast.success("تم إرسال إشعار تجريبي إليك");
      else toast.info("لم تُرسل أي إشعارات — تأكد أنك فعّلت الإشعارات على هذا الجهاز أولاً من الإعدادات.");
    } catch (e) {
      toast.error(extractError(e, "فشل الاختبار"));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="admin-notifications">
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-semibold flex items-center gap-2">
          <Bell className="h-6 w-6 text-primary" /> إشعارات التطبيق
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          أرسل إشعارات فورية إلى الأعضاء المشتركين — مفتوحة المصدر بالكامل (VAPID)، بدون فايربيس أو خدمات مدفوعة.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4" data-testid="stat-total-subs">
          <div className="text-xs text-muted-foreground">إجمالي الأجهزة المشتركة</div>
          <div className="text-2xl font-display font-semibold mt-1">{stats.total_subscriptions}</div>
        </Card>
        <Card className="p-4" data-testid="stat-unique-users">
          <div className="text-xs text-muted-foreground">أعضاء فرديون</div>
          <div className="text-2xl font-display font-semibold mt-1 inline-flex items-center gap-1.5">
            <Users className="h-5 w-5 text-primary" /> {stats.unique_users}
          </div>
        </Card>
      </div>

      {/* Composer */}
      <Card className="p-5 space-y-4" data-testid="notif-composer">
        <div className="flex items-center gap-2">
          <Send className="h-4 w-4 text-primary" />
          <h2 className="font-display font-semibold">إرسال إشعار جديد</h2>
        </div>

        <div className="space-y-2">
          <Label>العنوان</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="مثال: وصفة جديدة اليوم!"
            maxLength={80}
            data-testid="notif-title"
          />
          <div className="text-[11px] text-muted-foreground">{title.length}/80</div>
        </div>

        <div className="space-y-2">
          <Label>نص الإشعار</Label>
          <Textarea
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="اكتب رسالة قصيرة وواضحة — الأجهزة تعرض أول 150 حرف تقريباً."
            maxLength={200}
            data-testid="notif-body"
          />
          <div className="text-[11px] text-muted-foreground">{body.length}/200</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>الرابط عند الضغط</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="/recipes"
              data-testid="notif-url"
            />
            <div className="text-[11px] text-muted-foreground">
              يفتح داخل التطبيق. أمثلة: <code>/recipes</code>, <code>/courses</code>, <code>/community</code>.
            </div>
          </div>
          <div className="space-y-2">
            <Label>الشريحة المستهدفة</Label>
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger data-testid="notif-audience">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع المشتركين</SelectItem>
                <SelectItem value="members">الأعضاء فقط (بدون الإدارة)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="rounded-lg border bg-secondary/40 p-3 space-y-1">
          <div className="text-[11px] text-muted-foreground">معاينة:</div>
          <div className="flex items-start gap-2">
            <div className="h-9 w-9 rounded-lg bg-primary/15 grid place-items-center shrink-0">
              <Bell className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{title || "عنوان الإشعار"}</div>
              <div className="text-xs text-muted-foreground line-clamp-2">{body || "نص الإشعار سيظهر هنا..."}</div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={broadcast}
            disabled={sending || !title.trim() || !body.trim() || stats.total_subscriptions === 0}
            className="gap-2 rounded-full"
            data-testid="btn-notif-send"
          >
            <Send className="h-4 w-4" />
            {sending ? "جارٍ الإرسال..." : `أرسل الآن (${stats.total_subscriptions} جهاز)`}
          </Button>
          <Button
            variant="outline"
            onClick={sendTest}
            disabled={testing}
            className="gap-2 rounded-full"
            data-testid="btn-notif-test"
          >
            <TestTube2 className="h-4 w-4" />
            {testing ? "جارٍ..." : "أرسل اختباراً لي"}
          </Button>
        </div>

        {lastResult && (
          <div className="rounded-lg border-2 border-primary/40 bg-primary/5 p-3 text-sm space-y-0.5" data-testid="notif-result">
            <div><b>تم بنجاح:</b> {lastResult.sent} جهاز</div>
            {!!lastResult.failed && <div className="text-amber-700 dark:text-amber-400">فشل مؤقت: {lastResult.failed}</div>}
            {!!lastResult.pruned && <div className="text-muted-foreground text-xs">حُذفت {lastResult.pruned} اشتراكاً منتهياً تلقائياً.</div>}
          </div>
        )}

        {stats.total_subscriptions === 0 && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs text-amber-800 dark:text-amber-200">
            لا يوجد مشتركون بعد. اطلب من الأعضاء تفعيل الإشعارات من لوحتهم الرئيسية أو من صفحة الإعدادات.
          </div>
        )}
      </Card>

      <Card className="p-5 space-y-2 text-sm" data-testid="notif-auto-events">
        <div className="font-display font-semibold">إشعارات تلقائية</div>
        <p className="text-muted-foreground text-xs">هذه تعمل تلقائياً بدون تدخل منك:</p>
        <ul className="list-disc ps-5 text-xs text-muted-foreground space-y-1">
          <li>رسالة جديدة من المدرب → تُبلَّغ العضو المستهدف.</li>
          <li>تعليق جديد على منشور → يُبلَّغ صاحب المنشور.</li>
          <li>نشر وصفة جديدة → تُبلَّغ جميع المشتركين.</li>
          <li>إضافة درس جديد لدورة → يُبلَّغ الأعضاء المسجلون فيها.</li>
          <li>اشتراك سينتهي خلال 3 أيام → يُبلَّغ العضو (تُرسل مرة واحدة لكل دورة اشتراك).</li>
        </ul>
      </Card>
    </div>
  );
}
